import { useState, useRef, useEffect } from "react";
import type { TypographySuggestion } from "../types";

interface Props {
  imageDataUrl: string;
  quoteText: string;
  author?: string;
  suggestions: TypographySuggestion[];
  onClose: () => void;
}

interface TextConfig {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  color: string;
  x: number;
  y: number;
  textAlign: CanvasTextAlign;
  lineHeight: number;
  maxWidth: number;
  showAuthor: boolean;
  authorFontSize: number;
}

const FONT_OPTIONS = [
  { value: "Georgia, serif", label: "Georgia (Serif)" },
  { value: "Arial, sans-serif", label: "Arial (Sans-serif)" },
  { value: "Verdana, sans-serif", label: "Verdana" },
  { value: "Times New Roman, serif", label: "Times New Roman" },
  { value: "Courier New, monospace", label: "Courier New" },
  { value: "Impact, sans-serif", label: "Impact" },
  { value: "Comic Sans MS, cursive", label: "Comic Sans" },
  { value: "Trebuchet MS, sans-serif", label: "Trebuchet" },
  { value: "Palatino, serif", label: "Palatino" },
  { value: "Lucida Console, monospace", label: "Lucida Console" },
];

const POSITION_PRESETS = [
  { label: "Top Center", x: 0.5, y: 0.15 },
  { label: "Center", x: 0.5, y: 0.5 },
  { label: "Bottom Center", x: 0.5, y: 0.85 },
  { label: "Top Left", x: 0.2, y: 0.15 },
  { label: "Top Right", x: 0.8, y: 0.15 },
  { label: "Bottom Left", x: 0.2, y: 0.85 },
  { label: "Bottom Right", x: 0.8, y: 0.85 },
];

export function TypographyEditor({
  imageDataUrl,
  quoteText,
  author,
  suggestions,
  onClose,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 1024, height: 1024 });

  const [config, setConfig] = useState<TextConfig>({
    fontFamily: "Georgia, serif",
    fontSize: 48,
    fontWeight: "normal",
    color: "#ffffff",
    x: 0.5,
    y: 0.5,
    textAlign: "center",
    lineHeight: 1.4,
    maxWidth: 0.8,
    showAuthor: !!author,
    authorFontSize: 32,
  });

  const [shadowEnabled, setShadowEnabled] = useState(true);
  const shadowColor = "#000000";
  const shadowBlur = 4;

  // Load image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setCanvasSize({ width: img.width, height: img.height });
      setImageLoaded(true);
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  // Render canvas
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = imageRef.current;

    // Clear and draw image
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    // Configure text style
    ctx.font = `${config.fontWeight} ${config.fontSize}px ${config.fontFamily}`;
    ctx.fillStyle = config.color;
    ctx.textAlign = config.textAlign;
    ctx.textBaseline = "middle";

    // Shadow
    if (shadowEnabled) {
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
    } else {
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
    }

    const maxWidth = canvas.width * config.maxWidth;
    const x = canvas.width * config.x;
    let y = canvas.height * config.y;

    // Word wrap the quote
    const words = quoteText.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);

    // Calculate total height
    const lineHeightPx = config.fontSize * config.lineHeight;
    const totalHeight = lines.length * lineHeightPx;
    const startY = y - totalHeight / 2 + lineHeightPx / 2;

    // Draw quote lines
    lines.forEach((line, i) => {
      ctx.fillText(line, x, startY + i * lineHeightPx);
    });

    // Draw author
    if (config.showAuthor && author) {
      ctx.font = `italic ${config.authorFontSize}px ${config.fontFamily}`;
      const authorY = startY + lines.length * lineHeightPx + config.authorFontSize;
      ctx.fillText(`— ${author}`, x, authorY);
    }
  }, [imageLoaded, config, quoteText, author, shadowEnabled]);

  const applySuggestion = (suggestion: TypographySuggestion) => {
    // Parse suggestion and apply settings
    const newConfig = { ...config };

    // Try to extract font from suggestion
    const fontLower = (suggestion.fontRecommendation || "").toLowerCase();
    if (fontLower.includes("serif") && !fontLower.includes("sans")) {
      newConfig.fontFamily = "Georgia, serif";
    } else if (fontLower.includes("sans")) {
      newConfig.fontFamily = "Arial, sans-serif";
    } else if (fontLower.includes("script") || fontLower.includes("cursive")) {
      newConfig.fontFamily = "Comic Sans MS, cursive";
    } else if (fontLower.includes("impact") || fontLower.includes("bold")) {
      newConfig.fontFamily = "Impact, sans-serif";
      newConfig.fontWeight = "bold";
    }

    // Try to extract position
    const posLower = (suggestion.positioning || "").toLowerCase();
    if (posLower.includes("top")) {
      newConfig.y = 0.15;
    } else if (posLower.includes("bottom")) {
      newConfig.y = 0.85;
    } else {
      newConfig.y = 0.5;
    }

    if (posLower.includes("left")) {
      newConfig.x = 0.2;
      newConfig.textAlign = "left";
    } else if (posLower.includes("right")) {
      newConfig.x = 0.8;
      newConfig.textAlign = "right";
    } else {
      newConfig.x = 0.5;
      newConfig.textAlign = "center";
    }

    // Try to extract color
    const colorLower = (suggestion.colorScheme || "").toLowerCase();
    if (colorLower.includes("white")) {
      newConfig.color = "#ffffff";
    } else if (colorLower.includes("black")) {
      newConfig.color = "#000000";
    } else if (colorLower.includes("gold")) {
      newConfig.color = "#ffd700";
    }

    setConfig(newConfig);
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;

    const link = document.createElement("a");
    link.download = `typography-${Date.now()}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  const updateConfig = (updates: Partial<TextConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Typography Editor</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-auto flex flex-col">
          {/* Canvas Preview - Always on top */}
          <div className="p-4 bg-gray-100 flex items-center justify-center">
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className="max-w-full max-h-[40vh] shadow-lg"
              style={{ width: "auto", height: "auto" }}
            />
          </div>

          {/* Controls - Below the image */}
          <div className="p-4 border-t overflow-y-auto max-h-[45vh]">
            {/* Quick Apply Suggestions */}
            {suggestions.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apply Suggestion
                </label>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => applySuggestion(s)}
                      className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      {s.layout}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Controls Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Font */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Font Family
                </label>
                <select
                  value={config.fontFamily}
                  onChange={(e) => updateConfig({ fontFamily: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Font Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Font Size: {config.fontSize}px
                </label>
                <input
                  type="range"
                  min="16"
                  max="120"
                  value={config.fontSize}
                  onChange={(e) =>
                    updateConfig({ fontSize: parseInt(e.target.value) })
                  }
                  className="w-full"
                />
              </div>

              {/* Font Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Font Weight
                </label>
                <select
                  value={config.fontWeight}
                  onChange={(e) => updateConfig({ fontWeight: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="normal">Normal</option>
                  <option value="bold">Bold</option>
                </select>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Text Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.color}
                    onChange={(e) => updateConfig({ color: e.target.value })}
                    className="w-10 h-10 rounded border"
                  />
                  <input
                    type="text"
                    value={config.color}
                    onChange={(e) => updateConfig({ color: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded text-sm"
                  />
                </div>
              </div>

              {/* Max Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Width: {Math.round(config.maxWidth * 100)}%
                </label>
                <input
                  type="range"
                  min="30"
                  max="100"
                  value={config.maxWidth * 100}
                  onChange={(e) =>
                    updateConfig({ maxWidth: parseInt(e.target.value) / 100 })
                  }
                  className="w-full"
                />
              </div>

              {/* Shadow & Author */}
              <div className="space-y-2">
                <label className="flex items-center text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={shadowEnabled}
                    onChange={(e) => setShadowEnabled(e.target.checked)}
                    className="mr-2"
                  />
                  Text Shadow
                </label>
                {author && (
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={config.showAuthor}
                      onChange={(e) =>
                        updateConfig({ showAuthor: e.target.checked })
                      }
                      className="mr-2"
                    />
                    Show Author
                  </label>
                )}
              </div>
            </div>

            {/* Position Presets - Full width */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-9 gap-1">
                {POSITION_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() =>
                      updateConfig({
                        x: p.x,
                        y: p.y,
                        textAlign: p.x < 0.4 ? "left" : p.x > 0.6 ? "right" : "center",
                      })
                    }
                    className={`px-2 py-1 text-xs border rounded hover:bg-gray-100 ${
                      config.x === p.x && config.y === p.y
                        ? "bg-blue-100 border-blue-500"
                        : ""
                    }`}
                  >
                    {p.label.split(" ")[0]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
          >
            Download Image
          </button>
        </div>
      </div>
    </div>
  );
}
